import React, { useState, useEffect, useMemo, useCallback } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import "./OfferRegistry.css";
import { Routes, Route, useNavigate } from "react-router-dom";
import AddOfferRegistry from "./AddOfferRegistry";
import EditOfferRegistry from "./EditOfferRegistry";
import OfferDeleteDialog from "./OfferDeleteDialog";
import HistoryOffer from "./HistoryOffer";
import CreateOfferRevision from "./CreateOfferRevision";

const getPathFromUrl = (url) => {
  try {
    if (url.startsWith("/")) return url;
    const { pathname } = new URL(url);
    return pathname;
  } catch (error) {
    console.error("Eroare la extragerea URL-ului:", error);
    return url;
  }
};

const formatDateToRomanian = (dateString) => {
  if (!dateString || dateString === "") return "";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const formatAmountRO = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim();
  const match = raw.match(/-?[\d.,\s]+/);
  if (!match) return raw;

  const numericPart = match[0].trim();
  let normalized = numericPart.replace(/\s/g, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const n = Number(normalized);
  if (Number.isNaN(n)) return raw;

  const formatted = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  const suffix = raw.replace(match[0], "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const uniqueNonEmpty = (arr) => {
  const set = new Set();
  (arr || []).forEach((x) => {
    const val = (x ?? "").toString().trim();
    if (val) set.add(val);
  });
  return Array.from(set);
};

const getStatusClass = (status) => {
  switch (status) {
    case "Under client review":
      return "status-under-review";
    case "Contract signed":
      return "status-signed";
    case "On hold":
      return "status-on-hold";
    case "Offer closed":
      return "status-closed";
    default:
      return "status-default";
  }
};

const StatusPill = ({ value }) => {
  const cls = getStatusClass(value);
  return <span className={`status-pill ${cls}`}>{value || ""}</span>;
};

const HeaderTitle = (props) => <p className={props.class}>{props.title}</p>;

const HeaderAddButton = (props) => (
  <Button
    variant="contained"
    color="primary"
    onClick={() => props.navigate("/offer_registry/add-offer-registry")}
    startIcon={<AddIcon />}
    style={{ cursor: "pointer" }}
  >
    Add Offer
  </Button>
);

const Registry = (props) => {
  const { canEdit, canDelete, navigate } = props;

  const [registries, setRegistries] = useState([]);
  const [systemCurrency, setSystemCurrency] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");

  useEffect(() => {
    fetchOffers();
    fetchSystemCurrency();
  }, []);

  const fetchSystemCurrency = async () => {
    try {
      const response = await axiosInstance.get("/system-currency/1/");
      if (response.data?.currency_label) {
        setSystemCurrency(response.data.currency_label);
      }
    } catch (error) {
      console.error("Error fetching system currency:", error);
    }
  };

  const fetchOffers = () => {
    axiosInstance
      .get("/offer-register/")
      .then((response) => {
        const rows = (response.data || [])
          .sort((a, b) => b.sequence_number - a.sequence_number)
          .map((offer) => {
            const opps = offer.offer_opportunities || [];

            const projectLabels = uniqueNonEmpty(
              opps.map((o) => o.project_code_label)
            ).join(", ");

            const opportunityLabels = uniqueNonEmpty(
              opps.map((o) => o.opportunity_label)
            ).join(", ");

            return {
              ...offer,
              offer_date_formatted: formatDateToRomanian(offer.offer_date),
              raw_offer_date: offer.offer_date,
              project_code_label: projectLabels,
              opportunity_labels_joined: opportunityLabels,
              total_display: offer.total_display ?? "",
              total_system: offer.total_system_display ?? "",
            };
          });

        setRegistries(rows);
      })
      .catch((error) => {
        console.error("Error fetching registries", error);
        setDeleteErrorMessage("Failed to fetch offers");
        setErrorDialogOpen(true);
      });
  };

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleHistoryClick = useCallback(
    (row) => {
      navigate(`/offer_registry/history/${row.id}`);
    },
    [navigate]
  );

  const handleCreateRevisionClick = useCallback(
    (row) => {
      navigate(`/offer_registry/create-revision/${row.id}`);
    },
    [navigate]
  );

  const handleExportDocx = useCallback(async (row) => {
    try {
      const response = await axiosInstance.get(
        `/offer-register/${row.id}/export-docx/`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const safeCode = String(row.offer_code || row.id).replace(/[\\/]/g, "_");
      link.href = url;
      link.setAttribute("download", `oferta_${safeCode}.docx`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting offer DOCX:", error);
      setDeleteErrorMessage(
        error?.response?.data?.detail ||
          "Failed to export the offer in DOCX format."
      );
      setErrorDialogOpen(true);
    }
  }, []);

  const registrycolumns = useMemo(() => {
    const cols = [
      { field: "offer_code", headerName: "Number", minWidth: 150 },

      {
        field: "project_code_label",
        headerName: "Project Code",
        minWidth: 220,
        flex: 1,
      },

      {
        field: "offer_date_formatted",
        headerName: "Offer date",
        minWidth: 150,
        sortComparator: (v1, v2, param1, param2) => {
          const row1 = registries.find((r) => r.id === param1.id);
          const row2 = registries.find((r) => r.id === param2.id);
          if (!row1 || !row2) return 0;

          const date1 = new Date(row1.raw_offer_date || "");
          const date2 = new Date(row2.raw_offer_date || "");
          return date1.getTime() - date2.getTime();
        },
      },

      { field: "client_name", headerName: "Client", minWidth: 200 },

      {
        field: "total_display",
        headerName: "Value (Original currency)",
        minWidth: 215,
        renderCell: (params) => (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatAmountRO(params.row.total_display)}
          </div>
        ),
      },

      {
        field: "total_system",
        headerName: systemCurrency ? `Value  (${systemCurrency})` : "Value ",
        minWidth: 180,
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              fontWeight: "500",
            }}
          >
            {formatAmountRO(params.row.total_system)}
          </div>
        ),
      },

      {
        field: "status",
        headerName: "Status",
        minWidth: 200,
        renderCell: (params) => <StatusPill value={params.value} />,
      },

      {
        field: "valability",
        headerName: "Validity",
        minWidth: 120,
        renderCell: (params) => {
          const v = params.value;
          if (v === null || v === undefined || v === "") return "";
          return Number(v) === 1 ? "1 day" : `${v} days`;
        },
      },

      {
        field: "description",
        headerName: "Description",
        minWidth: 250,
        flex: 1,
      },
    ];

    cols.push({
      field: "actions",
      headerName: "Action",
      width: 235,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => handleExportDocx(params.row)}
            sx={{ color: "#455a64" }}
            title="Export offer DOCX"
          >
            <DownloadIcon />
          </IconButton>

          {canEdit ? (
            <IconButton
              onClick={() =>
                navigate(`/offer_registry/edit-offer-registry/${params.row.id}`)
              }
              sx={{ color: "#1976d2" }}
              title="Edit offer"
            >
              <EditNoteSharpIcon />
            </IconButton>
          ) : null}

          {canEdit ? (
            <IconButton
              onClick={() => handleHistoryClick(params.row)}
              sx={{ color: "#6a1b9a" }}
              title="Revision history"
            >
              <HistoryIcon />
            </IconButton>
          ) : null}

          {canEdit ? (
            <IconButton
              onClick={() => handleCreateRevisionClick(params.row)}
              sx={{ color: "#2e7d32" }}
              title="Create revision"
            >
              <LibraryAddIcon />
            </IconButton>
          ) : null}

          {canDelete ? (
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              sx={{ color: "#d32f2f" }}
              title="Delete offer"
            >
              <DeleteIcon />
            </IconButton>
          ) : null}
        </>
      ),
    });

    return cols;
  }, [
    canEdit,
    canDelete,
    navigate,
    registries,
    systemCurrency,
    handleHistoryClick,
    handleCreateRevisionClick,
    handleExportDocx,
  ]);

  return (
    <div className="main-container">
      <DataGrid
        id="offer-registry-table"
        rows={registries}
        columns={registrycolumns}
        getRowId={(row) => row.id}
        initialState={{
          sorting: { sortModel: [{ field: "sequence_number", sort: "desc" }] },
          pagination: { paginationModel: { pageSize: 12 } },
        }}
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.4",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": { whiteSpace: "normal" },
          "& .MuiDataGrid-cell:focus": { outline: "none" },
          "& .MuiDataGrid-cell:focus-within": { outline: "none" },
          "& .Mui-selected:hover": {
            backgroundColor: "transparent !important",
          },
        }}
        getRowHeight={() => "auto"}
        rowHeight={40}
        pageSizeOptions={[10, 12, 20, 25]}
      />

      <OfferDeleteDialog
        open={openDeleteDialog}
        record={itemToDelete}
        onClose={() => {
          setOpenDeleteDialog(false);
          setItemToDelete(null);
        }}
        onDeleted={() => {
          setOpenDeleteDialog(false);
          setItemToDelete(null);
          fetchOffers();
        }}
      />

      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" style={{ color: "red" }}>
          Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {deleteErrorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            sx={{
              backgroundColor: "#e0e0e0",
              color: "#424242",
              "&:hover": { backgroundColor: "#bdbdbd" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const OfferRegistry = () => {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const requiredUrl = getPathFromUrl("/offer_registry");
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_create === true
      )
    );

    setCanEdit(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_edit === true
      )
    );

    setCanDelete(
      permissions.some(
        (page) =>
          getPathFromUrl(page.url) === requiredUrl && page.can_delete === true
      )
    );
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="offer-registry-page">
            <div className="header-container reg-header">
              <HeaderTitle class="leftside-title" title="OFFER REGISTER" />
              {canCreate ? <HeaderAddButton navigate={navigate} /> : null}
            </div>

            <Registry
              canEdit={canEdit}
              canDelete={canDelete}
              navigate={navigate}
            />
          </div>
        }
      />

      <Route path="add-offer-registry" element={<AddOfferRegistry />} />
      <Route path="edit-offer-registry/:id" element={<EditOfferRegistry />} />
      <Route path="history/:id" element={<HistoryOffer />} />
      <Route path="create-revision/:id" element={<CreateOfferRevision />} />
    </Routes>
  );
};

export default OfferRegistry;